/* SpawnAnything — thrown explosives (render side): a voxel hand grenade and a voxel explosive barrel that fly to the
   target of the god tools "Granate" / "Sprengfass", with a shadow blob growing on the ground below the impact point.

   The sim owns the flight (src/sim/features/thrown.js): every frame message carries the objects in flight (start,
   target, age in ticks, flight ticks d, kind, id). The pose is a pure function of those and the time since the throw,
   so replays, seeking and multiplayer draw the same arc. Time = age + alpha ticks (alpha = the unit interpolation
   factor), i.e. it runs on game time: pause, slow motion and bullet time slow it down exactly like the units, and the
   object touches the ground when the frame with the explosion arrives.

   planThrow() turns a click into the integer command (positions in 1/16 m, flight ticks). */
import * as THREE from 'three';
import { VoxModel, meshAO, modelCenter } from '../game/voxel.js';
import { noOutline } from '../render/outlinePass.js';
import { THROW_KINDS, THROW_CAP, THROW_MIN, THROW_MAX, THROW_STRIDE } from '../sim/features/thrown.js';

export { THROW_KINDS };
const TICK = 1 / 30, G = 26; // ticks; visual gravity (m/s²) — snappy comic arcs
const BOUNCE = 0.2;          // barrel: last 0.2 s of its flight are a small hop after the first touch
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const q16 = v => Math.round(v * 16);

/**
 * Throw command for tool k ('granate' | 'fass') at target (x, gy, z).
 * from = { x, y, z } hand position of the possessed unit, or null: then it comes from above / behind the camera.
 * cam = { x, z, dist } camera position and orbit distance. Returns { sx, sy, sz, x, z, d } (integers).
 */
export function planThrow(k, x, gy, z, from, cam) {
  let sx, sy, sz;
  if (from) { sx = from.x; sy = from.y; sz = from.z; }
  else {
    let dx = cam.x - x, dz = cam.z - z; const l = Math.hypot(dx, dz);
    if (l > 1e-3) { dx /= l; dz /= l; } else { dx = 0; dz = 1; }
    const D = cam.dist || 40;
    const back = k === 'fass' ? clamp(D * 0.1, 2, 7) : clamp(D * 0.35, 7, 28), up = k === 'fass' ? clamp(14 + D * 0.05, 14, 24) : clamp(D * 0.22, 5, 16);
    sx = x + dx * back; sy = gy + up; sz = z + dz * back;
  }
  const L = Math.hypot(sx - x, sy - gy, sz - z);
  const sec = k === 'fass' ? clamp(0.7 + L * 0.012, 0.75, 1.2) : clamp(0.45 + L * 0.016, 0.5, 1.1);
  return { sx: q16(sx), sy: q16(sy), sz: q16(sz), x: q16(x), z: q16(z), d: clamp(Math.round(sec / TICK), THROW_MIN, THROW_MAX) };
}

// small deterministic per-object numbers from its id (spin axis, phase): same look on every client and in replays
const hashId = (id, s) => { let h = Math.imul((id | 0) ^ s, 2654435761); h ^= h >>> 15; h = Math.imul(h, 2246822519); h ^= h >>> 13; return ((h >>> 0) % 10000) / 10000; };

/**
 * Pose at time t (s since the throw) for frame record f[j..]: writes position into p, rotation into q; returns the
 * flight fraction 0..1. hh = half height of the model (its centre rests that high above the ground on landing).
 */
const _ax = new THREE.Vector3(), _q2 = new THREE.Quaternion(), _up = new THREE.Vector3(0, 1, 0);
export function thrownPose(f, j, t, hh, p, q) {
  const sx = f[j], sy = f[j + 1], sz = f[j + 2], tx = f[j + 3], ty = f[j + 4] + hh, tz = f[j + 5];
  const D = f[j + 7] * TICK, vk = f[j + 8], id = f[j + 9];
  let dx = tx - sx, dz = tz - sz; const l = Math.hypot(dx, dz); if (l > 1e-3) { dx /= l; dz /= l; } else { dx = 1; dz = 0; }
  const a0 = hashId(id, 1) * 6.283, wob = hashId(id, 2) - 0.5;
  if (vk === 1) { // barrel: falls in, lands upright-ish, small hop, explodes on the second touch
    const Df = Math.max(TICK, D - BOUNCE), tt = clamp(t, 0, D);
    if (tt < Df) {
      const u = tt / Df, vy0 = (ty - sy + 0.5 * G * Df * Df) / Df;
      p.set(sx + (tx - sx) * u, sy + vy0 * tt - 0.5 * G * tt * tt, sz + (tz - sz) * u);
      // tumbles slightly and rights itself towards the landing
      _ax.set(dz + wob * 0.6, 0, -dx).normalize();
      q.setFromAxisAngle(_ax, (Df - tt) * 2.4 * (wob < 0 ? -1 : 1));
    } else {
      const u = (tt - Df) / Math.max(1e-3, D - Df);
      p.set(tx, ty + 4 * 0.32 * u * (1 - u), tz);
      _ax.set(dz, 0, -dx); q.setFromAxisAngle(_ax, Math.sin(u * Math.PI) * 0.22 * (wob < 0 ? -1 : 1));
    }
    q.premultiply(_q2.setFromAxisAngle(_up, a0 + tt * 1.5 * wob));
    return tt / D;
  }
  // grenade: ballistic arc, tumbling end over end around an axis across the flight direction
  const tt = clamp(t, 0, D), u = tt / D, vy0 = (ty - sy + 0.5 * G * D * D) / D;
  p.set(sx + (tx - sx) * u, sy + vy0 * tt - 0.5 * G * tt * tt, sz + (tz - sz) * u);
  _ax.set(dz, wob * 0.5, -dx).normalize();
  q.setFromAxisAngle(_up, a0).premultiply(_q2.setFromAxisAngle(_ax, tt * (13 + wob * 6)));
  return u;
}

/* ---- voxel models ---- */
function grenadeModel() {
  const m = new VoxModel(0.055);
  const body = 0x4d6e31, groove = 0x2c4320, steel = 0x7f868e, lever = 0xb9bfc6, ring = 0xe8b830;
  // "pineapple" body: segment grooves every third row and column
  m.sphere(0, 4, 0, 3, (x, y, z) => ((y + 30) % 3 === 0 || (x + z + 30) % 3 === 0 ? groove : body), 1.3);
  m.box(-1, 8, -1, 1, 9, 1, steel);             // fuse head
  m.box(-1, 10, 0, 2, 10, 0, lever);            // lever over the top …
  m.box(3, 4, 0, 3, 10, 0, lever);              // … and down the side
  m.set(-2, 9, 0, steel);                       // pin
  for (const [x, y] of [[-3, 9], [-4, 10], [-4, 11], [-3, 12], [-2, 11], [-2, 10]]) m.set(x, y, 0, ring); // pin ring
  return m;
}
function barrelModel() {
  const m = new VoxModel(0.1);
  const red = 0xd1352b, rib = 0x7e1b16, lid = 0x5c1813, yel = 0xf3c22f, blk = 0x1d1d20, steel = 0x8a9097;
  for (let y = 0; y <= 13; y++) for (let x = -6; x <= 6; x++) for (let z = -6; z <= 6; z++) {
    const r2 = x * x + z * z, ribRow = y === 2 || y === 11;
    if (r2 > (ribRow ? 36 : 27)) continue;
    let c = red;
    if (ribRow) c = rib;
    else if (y >= 5 && y <= 8) c = (Math.floor((Math.atan2(z, x) / 6.2832 + 0.5) * 12 + y * 0.5) & 1) ? yel : blk; // hazard band
    if (y === 13 || y === 0) c = r2 > 16 ? rib : lid;
    m.set(x, y, z, c);
  }
  m.box(2, 14, 1, 3, 14, 2, steel); // bung on the lid
  return m;
}
function geoOf(m) {
  const g = meshAO(m, 0, modelCenter(m), 0.05);
  g.computeBoundingBox(); g.center(); g.computeBoundingBox(); g.computeBoundingSphere();
  return g;
}

/**
 * deps: { scene, toonGrad, onLand(x, z, vk, own) } — onLand fires once per object that reached its target.
 */
export function createThrownFx({ scene, toonGrad, onLand }) {
  const kinds = [grenadeModel(), barrelModel()].map(m => {
    const geo = geoOf(m), mat = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: toonGrad });
    const mesh = new THREE.InstancedMesh(geo, mat, THROW_CAP);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); mesh.frustumCulled = false; mesh.castShadow = true; mesh.count = 0;
    scene.add(mesh);
    return { mesh, hh: geo.boundingBox.max.y, r: geo.boundingSphere.radius };
  });
  // shadow blobs at the impact points (one instanced dark disc each)
  const disc = new THREE.CircleGeometry(1, 20); disc.rotateX(-Math.PI / 2);
  const blobMat = noOutline(new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.42, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
  const blobs = new THREE.InstancedMesh(disc, blobMat, THROW_CAP * 2);
  blobs.instanceMatrix.setUsage(THREE.DynamicDrawUsage); blobs.frustumCulled = false; blobs.renderOrder = 1; blobs.count = 0;
  scene.add(blobs);

  let data = null, prev = new Map();
  const _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _m = new THREE.Matrix4(), _v = new THREE.Vector3(), _q0 = new THREE.Quaternion();
  return {
    /** frame message field `th` (Float32Array or null) */
    frame(th) {
      const now = new Map();
      if (th) for (let j = 0; j < th.length; j += THROW_STRIDE) now.set(th[j + 9], j);
      // an object that was on its last tick and is gone now has landed (its explosion is in this frame's events)
      for (const [id, o] of prev) if (!now.has(id) && o.age >= o.d - 2) onLand && onLand(o.x, o.z, o.vk, o.own);
      prev = new Map();
      if (th) for (const [id, j] of now) prev.set(id, { x: th[j + 3], z: th[j + 5], age: th[j + 6], d: th[j + 7], vk: th[j + 8], own: th[j + 10] === 1 });
      data = th;
    },
    /** per render frame: alpha = unit interpolation factor 0..1, cam = camera position (visibility scale) */
    update(alpha, cam) {
      const n = [0, 0]; let nb = 0;
      if (data) for (let j = 0; j < data.length; j += THROW_STRIDE) {
        const vk = data[j + 8] | 0, K = kinds[vk]; if (!K) continue;
        const t = (data[j + 6] + alpha) * TICK;
        const u = thrownPose(data, j, t, K.hh, _p, _q);
        // far from the camera the object grows a little so it stays readable (comic exaggeration)
        const sc = clamp(_p.distanceTo(cam) / 40, 1, 4);
        _m.compose(_p, _q, _s.setScalar(sc)); _m.toArray(K.mesh.instanceMatrix.array, n[vk]++ * 16);
        // shadow on the ground under the target: grows and darkens as the object comes down
        const rr = K.r * sc * (0.35 + 0.65 * u);
        _m.compose(_v.set(data[j + 3], data[j + 4] + 0.05, data[j + 5]), _q0, _s.set(rr, 1, rr)); _m.toArray(blobs.instanceMatrix.array, nb++ * 16);
      }
      for (let k = 0; k < kinds.length; k++) { const M = kinds[k].mesh; M.count = n[k]; if (n[k]) M.instanceMatrix.needsUpdate = true; }
      blobs.count = nb; if (nb) blobs.instanceMatrix.needsUpdate = true;
    },
    /** new battle / renderer reset: nothing in flight until the next frame */
    reset() { data = null; prev = new Map(); for (const K of kinds) K.mesh.count = 0; blobs.count = 0; },
  };
}
